import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const SecureMessaging = ({ messages, onSendMessage, onViewThread }) => {
  const [showCompose, setShowCompose] = useState(false);
  const [newMessage, setNewMessage] = useState({
    subject: '',
    recipient: '',
    message: ''
  });

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return date?.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return date?.toLocaleDateString('en-US', { weekday: 'short' });
    } else {
      return date?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  };

  const handleSend = () => {
    if (newMessage?.subject && newMessage?.recipient && newMessage?.message) {
      onSendMessage(newMessage);
      setNewMessage({ subject: '', recipient: '', message: '' });
      setShowCompose(false);
    }
  };

  return (
    <div className="bg-card rounded-xl border border-border p-4 md:p-6">
      <div className="flex items-center justify-between mb-4 md:mb-6">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 md:w-12 md:h-12 bg-primary/10 rounded-lg">
            <Icon name="MessageSquare" size={20} color="var(--color-primary)" />
          </div>
          <div>
            <h2 className="text-lg md:text-xl font-semibold text-foreground">Secure Messages</h2>
            <p className="text-xs md:text-sm text-muted-foreground">Communicate with your care team</p>
          </div>
        </div>
        <Button
          variant="default"
          size="sm"
          iconName="Plus"
          iconPosition="left"
          onClick={() => setShowCompose(!showCompose)}
        >
          <span className="hidden md:inline">New Message</span>
          <span className="md:hidden">New</span>
        </Button>
      </div>
      {showCompose && (
        <div className="bg-background rounded-lg border border-border p-4 mb-4 space-y-4">
          <Input
            label="Subject"
            type="text"
            placeholder="Enter message subject"
            value={newMessage?.subject}
            onChange={(e) => setNewMessage({ ...newMessage, subject: e?.target?.value })}
          />
          <Input
            label="To"
            type="text"
            placeholder="Select provider or department"
            value={newMessage?.recipient}
            onChange={(e) => setNewMessage({ ...newMessage, recipient: e?.target?.value })}
          />
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Message</label>
            <textarea
              className="w-full min-h-[120px] px-3 py-2 bg-background border border-input rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
              placeholder="Type your message here..."
              value={newMessage?.message}
              onChange={(e) => setNewMessage({ ...newMessage, message: e?.target?.value })}
            />
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="default"
              size="sm"
              iconName="Send"
              iconPosition="left"
              onClick={handleSend}
            >
              Send Message
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowCompose(false)}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
      <div className="space-y-2">
        {messages?.length === 0 ? (
          <div className="text-center py-8 md:py-12">
            <div className="flex items-center justify-center w-16 h-16 md:w-20 md:h-20 bg-muted rounded-full mx-auto mb-4">
              <Icon name="MessageSquareOff" size={32} className="text-muted-foreground" />
            </div>
            <p className="text-sm md:text-base text-muted-foreground mb-4">No messages yet</p>
            <Button variant="default" onClick={() => setShowCompose(true)} iconName="Plus" iconPosition="left">
              Send Your First Message
            </Button>
          </div>
        ) : (
          messages?.map((message) => (
            <button
              key={message?.id}
              onClick={() => onViewThread(message)}
              className="w-full bg-background rounded-lg border border-border p-4 transition-smooth hover:shadow-elevation-2 text-left"
            >
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                    <Icon name="User" size={20} color="var(--color-primary)" />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <h3 className="text-sm md:text-base font-semibold text-foreground truncate">
                      {message?.from}
                    </h3>
                    <span className="text-xs text-muted-foreground whitespace-nowrap">{formatDate(message?.date)}</span>
                  </div>
                  <p className="text-xs md:text-sm font-medium text-foreground mb-1 truncate">{message?.subject}</p>
                  <p className="text-xs md:text-sm text-muted-foreground line-clamp-2">{message?.preview}</p>
                  <div className="flex items-center gap-2 mt-2">
                    {message?.unread && (
                      <span className="text-xs font-medium px-2 py-1 rounded bg-primary/10 text-primary">
                        Unread
                      </span>
                    )}
                    {message?.hasAttachment && (
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Icon name="Paperclip" size={12} />
                        <span>Attachment</span>
                      </div>
                    )}
                  </div>
                </div>
                <Icon name="ChevronRight" size={18} className="text-muted-foreground flex-shrink-0" />
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  );
};

export default SecureMessaging;